/**
 * Courses Service (MIRA 2.0)
 *
 * Maps snake_case API responses → camelCase TS types.
 * All functions throw ApiError on failure; callers should let errors propagate
 * to the hook layer where they're translated into UI state.
 *
 * Mapper functions are exported so they can be unit-tested.
 */

import { apiGet, apiPost, apiDelete } from './api';
import type {
  Course,
  CourseDetail,
  MyEnrollment,
  EnrollmentWithCourse,
  PendingEnrollment,
  SalonEnrollment,
  SalonEmployee,
  EnrollResult,
  CancelResult,
  BulkEnrollResult,
  BulkCancelResult,
} from '../types/courses.types';

// =============================================================================
// Raw API shapes (snake_case as returned by MIRA 2.0)
// =============================================================================

interface RawInstructor {
  id: number;
  name: string;
  description: string | null;
}

interface RawCourseImage {
  id: number;
  url: string;
  alt_text: string | null;
  sort_order: number;
}

interface RawCourse {
  slug: string;
  title: string;
  course_date: string;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  location_tbd: boolean;
  course_type: 'internal' | 'external';
  price: string;
  external_price: string | null;
  max_participants: number | null;
  enrolled_count: number;
  available_spots: number | null;
  description: string | null;
  image_url: string | null;
  // Forward-compat: MIRA's model-task endpoint already exposes this shape.
  // /portal/courses will gain it; we read it when present, else fall back to image_url.
  images?: RawCourseImage[];
  instructors: RawInstructor[];
  status: 'active' | 'draft' | 'archived' | 'cancelled';
  cancellation_hours: number;
}

interface RawMyEnrollment {
  id: number;
  status: MyEnrollment['status'];
  requested_at: string;
  processed_at: string | null;
}

interface RawEnrollmentWithCourse {
  id: number;
  course_slug: string;
  course_title: string;
  course_date: string;
  course_price: string;
  course_location: string | null;
  status: MyEnrollment['status'];
  requested_at: string;
  processed_at: string | null;
}

interface RawPendingEnrollment {
  id: number;
  employee_id: number;
  employee_name: string;
  status: 'pending';
  created_at: string;
  course: {
    slug: string;
    title: string;
    course_date: string;
    price: string;
  };
}

interface RawSalonEnrollment {
  id: number;
  employee_id: number;
  employee_name: string;
  status: MyEnrollment['status'];
  created_at: string;
  processed_at: string | null;
  registered_by_name: string | null;
  course: {
    slug: string;
    title: string;
    course_date: string;
    price: string;
    location: string | null;
  };
}

interface RawSalonEmployee {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  is_owner: boolean;
}

// =============================================================================
// Mappers (exported for unit tests)
// =============================================================================

function deriveImages(raw: RawCourse): string[] | undefined {
  if (raw.images && raw.images.length > 0) {
    return [...raw.images]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((img) => img.url);
  }
  return raw.image_url ? [raw.image_url] : undefined;
}

export function mapCourse(raw: RawCourse): Course {
  return {
    slug: raw.slug,
    productName: raw.title,
    price: Number(raw.price),
    externalPrice: raw.external_price !== null ? Number(raw.external_price) : null,
    courseDate: raw.course_date,
    startTime: raw.start_time,
    endTime: raw.end_time,
    location: raw.location,
    locationTbd: raw.location_tbd,
    courseType: raw.course_type,
    cancellationHours: raw.cancellation_hours,
    maxParticipants: raw.max_participants,
    enrolledCount: raw.enrolled_count,
    availableSpots: raw.available_spots,
    description: raw.description,
    imageUrl: raw.image_url,
    images: deriveImages(raw),
    instructors: raw.instructors.map((i) => ({
      id: i.id,
      name: i.name,
      description: i.description,
    })),
    status: raw.status,
  };
}

export function mapMyEnrollment(raw: RawMyEnrollment): MyEnrollment {
  return {
    id: raw.id,
    status: raw.status,
    requestedAt: raw.requested_at,
    processedAt: raw.processed_at,
  };
}

export function mapEnrollmentWithCourse(raw: RawEnrollmentWithCourse): EnrollmentWithCourse {
  return {
    id: raw.id,
    courseSlug: raw.course_slug,
    courseProductName: raw.course_title,
    courseDate: raw.course_date,
    coursePrice: Number(raw.course_price),
    courseLocation: raw.course_location,
    status: raw.status,
    requestedAt: raw.requested_at,
    processedAt: raw.processed_at,
  };
}

export function mapPending(raw: RawPendingEnrollment): PendingEnrollment {
  return {
    id: raw.id,
    employeeId: raw.employee_id,
    employeeName: raw.employee_name,
    courseSlug: raw.course.slug,
    courseProductName: raw.course.title,
    courseDate: raw.course.course_date,
    coursePrice: Number(raw.course.price),
    requestedAt: raw.created_at,
  };
}

export function mapSalonEnrollment(raw: RawSalonEnrollment): SalonEnrollment {
  return {
    id: raw.id,
    employeeId: raw.employee_id,
    employeeName: raw.employee_name,
    courseSlug: raw.course.slug,
    courseProductName: raw.course.title,
    courseDate: raw.course.course_date,
    coursePrice: Number(raw.course.price),
    courseLocation: raw.course.location,
    status: raw.status,
    requestedAt: raw.created_at,
    processedAt: raw.processed_at,
    registeredByName: raw.registered_by_name,
  };
}

export function mapSalonEmployee(raw: RawSalonEmployee): SalonEmployee {
  return {
    id: raw.id,
    name: raw.name,
    firstName: raw.first_name,
    lastName: raw.last_name,
    isOwner: raw.is_owner,
  };
}

// =============================================================================
// Endpoints
// =============================================================================

/** GET /portal/courses?include_past=… */
export async function getAllCourses(
  includePast = false
): Promise<Course[]> {
  const data = await apiGet<{ courses: RawCourse[] }>('/portal/courses', {
    query: { include_past: includePast },
  });
  return data.courses.map(mapCourse);
}

/** GET /portal/courses/{slug} */
export async function getCourseDetail(
  slug: string
): Promise<CourseDetail> {
  const data = await apiGet<{ course: RawCourse; myEnrollment: RawMyEnrollment | null }>(
    `/portal/courses/${encodeURIComponent(slug)}`
  );
  return {
    ...mapCourse(data.course),
    myEnrollment: data.myEnrollment ? mapMyEnrollment(data.myEnrollment) : null,
  };
}

/** GET /portal/me/enrollments */
export async function getMyEnrollments(): Promise<EnrollmentWithCourse[]> {
  const data = await apiGet<{ enrollments: RawEnrollmentWithCourse[] }>(
    '/portal/me/enrollments'
  );
  return data.enrollments.map(mapEnrollmentWithCourse);
}

/** POST /portal/courses/{slug}/enrollments */
export async function enrollInCourse(
  slug: string
): Promise<EnrollResult> {
  const data = await apiPost<{
    enrollment_id: number;
    status: MyEnrollment['status'];
    requires_approval: boolean;
  }>(`/portal/courses/${encodeURIComponent(slug)}/enrollments`);
  return {
    enrollmentId: data.enrollment_id,
    status: data.status,
    requiresApproval: data.requires_approval,
  };
}

/** DELETE /portal/enrollments/{id} */
export async function cancelEnrollment(
  enrollmentId: number
): Promise<CancelResult> {
  const data = await apiDelete<{ enrollment_id: number; new_status: MyEnrollment['status'] }>(
    `/portal/enrollments/${enrollmentId}`
  );
  return { enrollmentId: data.enrollment_id, newStatus: data.new_status };
}

/** POST /portal/enrollments/{id}/approve  (owner only) */
export async function approveEnrollment(
  enrollmentId: number
): Promise<{ status: MyEnrollment['status'] }> {
  return apiPost<{ status: MyEnrollment['status'] }>(
    `/portal/enrollments/${enrollmentId}/approve`
  );
}

/** POST /portal/enrollments/{id}/reject  (owner only) */
export async function rejectEnrollment(
  enrollmentId: number
): Promise<{ status: MyEnrollment['status'] }> {
  return apiPost<{ status: MyEnrollment['status'] }>(
    `/portal/enrollments/${enrollmentId}/reject`
  );
}

/** GET /portal/salon/employees  (owner only) */
export async function getSalonEmployees(): Promise<{ employees: SalonEmployee[]; salonName: string }> {
  const data = await apiGet<{ employees: RawSalonEmployee[]; salon_name: string }>(
    '/portal/salon/employees'
  );
  return {
    employees: data.employees.map(mapSalonEmployee),
    salonName: data.salon_name,
  };
}

/** POST /portal/courses/{slug}/enrollments/bulk  (owner only) */
export async function bulkEnroll(
  slug: string,
  employeeIds: number[]
): Promise<BulkEnrollResult> {
  const data = await apiPost<{
    enrollments: Array<{ enrollment_id: number; employee_id: number }>;
    skipped: Array<{ employee_id: number; reason: string }>;
    total_enrolled: number;
  }>(`/portal/courses/${encodeURIComponent(slug)}/enrollments/bulk`, {
    body: { employee_ids: employeeIds },
  });
  return {
    enrollments: data.enrollments.map((e) => ({
      enrollmentId: e.enrollment_id,
      employeeId: e.employee_id,
    })),
    skipped: data.skipped.map((s) => ({ employeeId: s.employee_id, reason: s.reason })),
    totalEnrolled: data.total_enrolled,
  };
}

/** POST /portal/enrollments/bulk-cancel  (owner only) */
export async function bulkCancelEnrollments(
  enrollmentIds: number[]
): Promise<BulkCancelResult> {
  const data = await apiPost<{
    cancelled: Array<{
      enrollment_id: number;
      employee_name: string;
      new_status: MyEnrollment['status'];
    }>;
    skipped: Array<{ enrollment_id: number; reason: string }>;
    total_cancelled: number;
    awaiting_credit_count: number;
  }>('/portal/enrollments/bulk-cancel', {
    body: { enrollment_ids: enrollmentIds },
  });
  return {
    cancelled: data.cancelled.map((c) => ({
      enrollmentId: c.enrollment_id,
      employeeName: c.employee_name,
      newStatus: c.new_status,
    })),
    skipped: data.skipped.map((s) => ({ enrollmentId: s.enrollment_id, reason: s.reason })),
    totalCancelled: data.total_cancelled,
    awaitingCreditCount: data.awaiting_credit_count,
  };
}

/** GET /portal/salon/enrollments?course_slug=…  (owner only) */
export async function getMySalonEnrollments(
  courseSlug?: string
): Promise<{
  enrollments: SalonEnrollment[];
  salonName: string;
  isOwner: boolean;
  totalCount: number;
}> {
  const data = await apiGet<{
    enrollments: RawSalonEnrollment[];
    salon_name: string;
    is_owner: boolean;
    total_count: number;
  }>('/portal/salon/enrollments', {
    query: courseSlug ? { course_slug: courseSlug } : undefined,
  });
  return {
    enrollments: data.enrollments.map(mapSalonEnrollment),
    salonName: data.salon_name,
    isOwner: data.is_owner,
    totalCount: data.total_count,
  };
}
